import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

export default function WorkerSelect({ value, onChange, onWorkerSelect, placeholder, label, required }) {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    api.get(`/workers${params}`)
      .then(r => setWorkers(r.data.data || []))
      .catch(() => setWorkers([]))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { setSearch(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (worker) => {
    const name = `${worker.first_name} ${worker.last_name}`;
    setSearch(name);
    onChange(name);
    if (onWorkerSelect) onWorkerSelect(worker);
    setOpen(false);
  };

  const filtered = workers.filter(w =>
    `${w.first_name} ${w.last_name}`.toLowerCase().includes((search || '').toLowerCase())
  );
  const own = filtered.filter(w => w.is_own);
  const regional = filtered.filter(w => !w.is_own);

  const renderRow = (w) => {
    const idBadge = { tc: '\u{1F1F9}\u{1F1F7}', yabanci: '\u{1F30D}', gecici: '\u{1F477}' }[w.identity_type] || '';
    return (
      <div key={w.id} onClick={() => handleSelect(w)}
        style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <span style={{ fontSize: '0.85rem' }}>{idBadge}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{w.first_name} {w.last_name}</div>
          <div style={{ fontSize: '0.72rem', color: '#999' }}>
            {w.default_daily_rate ? `₺${w.default_daily_rate}/${w.preferred_unit || 'gün'}` : ''}
            {w.phone && ` · ${w.phone}`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="form-group" ref={wrapperRef} style={{ position: 'relative' }}>
      {label && <label>{label}{required && ' *'}</label>}
      <input value={search}
        onChange={e => { setSearch(e.target.value); onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder={placeholder || 'İşçi ara...'} required={required} autoComplete="off" />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
          maxHeight: 250, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {loading && <div style={{ padding: 10, color: '#999', fontSize: '0.82rem' }}>{t('common.loading')}</div>}
          {own.length > 0 && (
            <>
              <div style={{ padding: '5px 12px', fontSize: '0.7rem', fontWeight: 700, color: 'var(--green-mid)', background: '#f0f7ee' }}>
                {'\u{1F464}'} Kendi {'İ'}{'şç'}ilerim
              </div>
              {own.map(renderRow)}
            </>
          )}
          {regional.length > 0 && (
            <>
              <div style={{ padding: '5px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#3498db', background: '#eef6ff' }}>
                {'\u{1F310}'} B{'ö'}lgesel {'İ'}{'şç'}iler
              </div>
              {regional.map(renderRow)}
            </>
          )}
          {!loading && filtered.length === 0 && search && (
            <div style={{ padding: '8px 12px', fontSize: '0.82rem', color: '#888' }}>
              Kay{'ı'}tl{'ı'} i{'şç'}i bulunamad{'ı'}. {'İ'}smi yazarak devam edebilirsiniz.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
